import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { Button } from "./Button.tsx";
import type { ChartTone } from "./Charts.tsx";
import { Icon } from "./Icon.tsx";
import { cx } from "./lib/cx.ts";

// See Design > UI, "Workspace". A working page in a flush AppLayout: a PageBar,
// then a Workspace of the page's work and, optionally, a DockPanel beside it. The
// page scrolls as one; nothing inside needs a scrollbar of its own.

/** True once the window has scrolled. */
function usePageScrolled(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(scrollY > 0);
    update();
    addEventListener("scroll", update, { passive: true });
    return () => removeEventListener("scroll", update);
  }, []);
  return scrolled;
}

/**
 * The page's own bar: where you are on the left, the page's controls on the
 * right. Rule: it stays at the top, transparent until content scrolls under it,
 * then glass with one hairline. One solid button at most, as everywhere.
 */
export function PageBar({ crumbs, title, children }: { crumbs?: ComponentChildren; title: ComponentChildren; children?: ComponentChildren }) {
  const scrolled = usePageScrolled();
  return <header class="ui-pagebar" data-scrolled={scrolled ? "" : undefined}>
    <div class="ui-pagebar__title">{crumbs && <span class="ui-pagebar__crumbs">{crumbs}</span>}<h1>{title}</h1></div>
    {children && <div class="ui-pagebar__actions">{children}</div>}
  </header>;
}

/** The page's work, and the DockPanel beside it when one is open. */
export function Workspace({ panel, children }: { panel?: ComponentChildren; children: ComponentChildren }) {
  return <div class={cx("ui-workspace", panel ? "ui-workspace--panel" : undefined)}>
    <div class="ui-workspace__main">{children}</div>
    {panel}
  </div>;
}

/**
 * The docked panel: it follows what's selected in the work and is where you act
 * on it. Rule: it sits on the page's own ground behind one hairline, keeps its
 * place under the PageBar while it fits on screen, and scrolls with the page
 * when it's longer, so it never needs a second scrollbar.
 */
export function DockPanel({ label, head, children }: { label: string; head: ComponentChildren; children: ComponentChildren }) {
  const inner = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(true);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    const check = () => setFits(el.offsetHeight <= innerHeight - 72);
    const watch = new ResizeObserver(check);
    watch.observe(el);
    addEventListener("resize", check);
    check();
    return () => { watch.disconnect(); removeEventListener("resize", check); };
  }, []);
  return <aside class="ui-dock" aria-label={label}>
    <div ref={inner} class="ui-dock__inner" data-sticky={fits ? "" : undefined}>
      <div class="ui-dock__head">{head}</div>
      <div class="ui-dock__body">{children}</div>
    </div>
  </aside>;
}

/** Shows or hides the DockPanel, for the PageBar. */
export function PanelToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return <Button variant="ghost" icon aria-label={open ? "Hide the side panel" : "Show the side panel"} aria-pressed={open} title={open ? "Hide panel" : "Show panel"} onClick={onToggle}><Icon name="panel" /></Button>;
}

export interface Figure {
  label: ComponentChildren; value: ComponentChildren; context: ComponentChildren;
  /** A quiet tint for the figure the page leads with: accent for money, violet for attention. */
  tone?: "accent" | "violet";
  /** Where the figure's action lives: a page (`href`) or something to open (`onSelect`). */
  action?: { label: string; href?: string; onSelect?: () => void };
}

/** A row of figures in one lifted strip, divided by hairlines. Rule: each figure names the action it informs, right under it. */
export function FigureStrip({ label, figures }: { label: string; figures: ReadonlyArray<Figure> }) {
  return <div class="ui-figures" role="group" aria-label={label}>
    {figures.map((f, i) => <div key={i} class={f.tone ? `ui-figures__item ui-figures__item--${f.tone}` : "ui-figures__item"}>
      <span class="ui-figures__label">{f.label}</span>
      <strong class="ui-figures__value">{f.value}</strong>
      <span class="ui-figures__context">{f.context}</span>
      {f.action && (f.action.href !== undefined
        ? <a class="ui-figures__action" href={f.action.href}>{f.action.label}</a>
        : <button type="button" class="ui-figures__action" onClick={f.action.onSelect}>{f.action.label}</button>)}
    </div>)}
  </div>;
}

export interface ShareRow { label: ComponentChildren; share: number; value: ComponentChildren; detail?: ComponentChildren; tone?: ChartTone }

/**
 * A contained breakdown: one row per part, each with a bar that runs from its
 * label toward its value, so the eye can follow it across. Rule: bars compare
 * rows within the list; the value states the number, so colour never carries it.
 */
export function ShareList({ title, rows }: { title?: ComponentChildren; rows: ReadonlyArray<ShareRow> }) {
  const top = Math.max(...rows.map((r) => r.share), 1);
  return <div class="ui-shares">
    {title && <p class="ui-shares__title">{title}</p>}
    <ul class="ui-shares__list">{rows.map((r, i) => <li key={i}>
      <span class="ui-shares__label">{r.label}</span>
      <span class={`ui-shares__track ui-chart-tone--${r.tone ?? "info"}`} aria-hidden="true"><span style={{ width: `${Math.max(4, (r.share / top) * 100)}%` }} /></span>
      <span class="ui-shares__value"><strong>{r.value}</strong>{r.detail !== undefined && <span>{r.detail}</span>}</span>
    </li>)}</ul>
  </div>;
}

/** One figure that leads a block, with what it's out of. */
export function LeadFigure({ value, label }: { value: ComponentChildren; label: ComponentChildren }) {
  return <p class="ui-lead"><strong>{value}</strong><span>{label}</span></p>;
}

/** A block in a DockPanel: a heading, an optional note on the right (such as the period), then its content. */
export function PanelBlock({ title, aside, children }: { title: ComponentChildren; aside?: ComponentChildren; children: ComponentChildren }) {
  return <section class="ui-panel-block"><div class="ui-panel-block__head"><h3>{title}</h3>{aside && <span>{aside}</span>}</div>{children}</section>;
}
